import { apiRequest } from '../api';
import { ARCHETYPE_ICONS } from '../archetype';
import { onLocaleChange, t } from '../i18n';
import type { TranslationKey } from '../i18n/types';

// ============================================================
// Страница «Все типажи»: распределение по лиге, подробное описание
// каждого типажа и его самые выраженные представители.
// ============================================================

interface ArchetypeTopPlayer {
    id: number;
    name: string;
    match: number;
    games: number;
    elo: number | null;
}

interface ArchetypeItem {
    id: string;
    count: number;
    percent: number;
    cell: string;
    players: ArchetypeTopPlayer[];
}

interface ArchetypesResponse {
    total: number;
    items: ArchetypeItem[];
}

const AXES: Array<{ key: 'aggression' | 'greed' | 'turtle'; label: TranslationKey }> = [
    { key: 'aggression', label: 'mystats.arch_axis.aggression' },
    { key: 'greed', label: 'mystats.arch_axis.greed' },
    { key: 'turtle', label: 'mystats.arch_axis.turtle' },
];

let data: ArchetypesResponse | null = null;
let loading: Promise<void> | null = null;
let requestSeq = 0;

function node<K extends keyof HTMLElementTagNameMap>(
    tag: K,
    className?: string,
    text?: string,
): HTMLElementTagNameMap[K] {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text !== undefined) el.textContent = text;
    return el;
}

onLocaleChange(() => {
    if (document.getElementById('screen-archetypes')?.classList.contains('hidden')) return;
    render();
});

export function mountArchetypes(): void {
    document.querySelectorAll<HTMLElement>('.screen').forEach((el) => {
        el.classList.toggle('hidden', el.id !== 'screen-archetypes');
    });

    const backBtn = document.getElementById('btn-back-archetypes');
    if (backBtn) {
        const fresh = backBtn.cloneNode(true) as HTMLElement;
        backBtn.replaceWith(fresh);
        fresh.addEventListener('click', () => {
            if (window.history.length > 1) window.history.back();
            else window.location.assign('/');
        });
    }

    renderSkeleton();
    if (data) {
        render();
        return;
    }
    loading ??= load().finally(() => {
        loading = null;
    });
    void loading;
}

export function unmountArchetypes(): void {
    requestSeq += 1;
}

async function load(): Promise<void> {
    const seq = ++requestSeq;
    try {
        const res = await apiRequest<ArchetypesResponse>('/api/archetypes');
        if (seq !== requestSeq) return;
        data = {
            total: Number(res.total) || 0,
            items: Array.isArray(res.items) ? res.items : [],
        };
        render();
    } catch {
        if (seq !== requestSeq) return;
        const container = document.getElementById('archetypes-container');
        if (container) container.textContent = t('archetypes.empty');
    }
}

function renderSkeleton(): void {
    const container = document.getElementById('archetypes-container');
    if (!container) return;
    container.innerHTML = '';
    container.appendChild(node('div', 'skeleton skeleton-block'));
}

function render(): void {
    const container = document.getElementById('archetypes-container');
    if (!container || !data) return;
    container.innerHTML = '';

    if (data.items.length === 0 || data.total === 0) {
        container.appendChild(node('p', 'hint', t('archetypes.empty')));
        return;
    }

    const grid = node('div', 'arch-grid');
    for (const item of data.items) grid.appendChild(buildCard(item));
    container.appendChild(grid);

    container.appendChild(
        node('p', 'hint arch-total', t('archetypes.total', { n: data.total })),
    );
}

function buildCard(item: ArchetypeItem): HTMLElement {
    const card = node('article', `arch-card${item.count === 0 ? ' is-empty' : ''}`);

    const head = node('header', 'arch-card-head');
    head.appendChild(node('span', 'arch-card-icon', ARCHETYPE_ICONS[item.id] ?? '🎭'));
    const title = node('div', 'arch-card-title');
    title.appendChild(
        node('h3', 'arch-card-name', t(`mystats.arch.${item.id}.name` as TranslationKey)),
    );
    title.appendChild(
        node(
            'span',
            'arch-card-count',
            item.count > 0
                ? t('archetypes.count', { n: item.count, percent: item.percent })
                : '—',
        ),
    );
    head.appendChild(title);
    card.appendChild(head);

    card.appendChild(
        node(
            'p',
            'arch-card-desc',
            t(`mystats.arch.${item.id}.desc` as TranslationKey),
        ),
    );

    // Полоса H/M/L по трём осям — из неё и складывается типаж.
    const cell = node('div', 'arch-card-cell');
    AXES.forEach((axis, index) => {
        const level = item.cell[index] ?? 'M';
        const chip = node('span', `arch-cell-chip is-${level.toLowerCase()}`);
        chip.append(
            node('span', 'arch-cell-axis', t(axis.label)),
            node(
                'span',
                'arch-cell-level',
                t(`archetypes.level.${level}` as TranslationKey),
            ),
        );
        cell.appendChild(chip);
    });
    card.appendChild(cell);

    const players = node('div', 'arch-card-players');
    players.appendChild(node('h4', 'arch-players-title', t('archetypes.players')));
    if (item.players.length === 0) {
        players.appendChild(node('p', 'hint', t('archetypes.no_players')));
    } else {
        const list = node('ul', 'arch-players-list');
        for (const player of item.players) {
            const row = node('li', 'arch-player');
            const link = node('a', 'arch-player-name', player.name);
            link.href = `/?player=${encodeURIComponent(player.name)}`;
            const match = node('span', 'arch-player-match', `${player.match}%`);
            const games = node(
                'span',
                'arch-player-games',
                t('archetypes.games', { n: player.games }),
            );
            row.append(link, match, games);
            list.appendChild(row);
        }
        players.appendChild(list);
    }
    card.appendChild(players);

    return card;
}
